import { InventoryQueryParams } from '@services/queries/equipments/getEquipments/makeParamsObject';
import {
  IPatchEquipmentVariables,
  patchEquipmentArray,
} from '@services/queries/equipments/patchEquipmentArray/patchEquipmentArray';
import {
  IPatchEquipmentArrayLambdaVariables,
  patchEquipmentArrayLambda,
} from '@services/queries/equipments/postEquipmentArrayLambda';
import { pick } from '@utils/pick';
import { IFormDataOnboardingModal } from '../OnBoardingModal';
import { SHARED_FIELDS_GLOBAL, SHARED_FIELDS } from '../OnBoardingModal.utils';
import {
  EquipmentTypeKeepNulls,
  PartialEquipmentTypeWithId,
  GlobalInput,
  GlobalEquipment,
} from '../types';

// REQ-UTILS
interface PatchMassEditLambda {
  equipment: Partial<GlobalEquipment>;
  studioId: string;
  queryParams: Partial<InventoryQueryParams> | undefined;
  tagsDiff?: LambdaTags;
  // Used to reset.
  dirtyGlobalFields: GlobalInput['dirtyGlobalFields'];
  dirtyFields: IFormDataOnboardingModal['dirtyFields'];
  deselectedItems: string[];
}

export interface MassEditShared {
  params: PartialEquipmentTypeWithId[];
  dirtyFields: IFormDataOnboardingModal['dirtyFields'];
}

/** This is another query, wrapping patchEquipmentArrayMassEdit. Same separation of business logic rules apply. */
export async function patchEquipmentArrayMassEdit({ params }: MassEditShared) {
  const _equipments = mapToPatchArray(params);
  const res = await patchEquipmentArray({
    data: { equipments: _equipments },
  });
  return res;
}

/** This is another query, wrapping patchEquipmentArrayLambda. Same separation of business logic rules apply. */
export async function patchEquipmentArrayLambdaWrap(
  params: PatchMassEditLambda,
) {
  const { equipment, studioId, queryParams, tagsDiff, deselectedItems } =
    params;

  const lambdaParams = getLambdaParams({
    studioId,
    equipment,
    query: queryParams,
    tagsDiff,
    deselectedItems,
  });
  const res = await patchEquipmentArrayLambda(lambdaParams);
  return res;
}

export type LambdaTags = NonNullable<
  IPatchEquipmentArrayLambdaVariables['tags']
>;
interface GetLambdaEquipmentArgs {
  studioId: string;
  equipment: Partial<GlobalEquipment>;
  deselectedItems: string[];
  query?: Partial<InventoryQueryParams>;
  tagsDiff?: LambdaTags;
}

function getLambdaParams(
  args: GetLambdaEquipmentArgs,
): IPatchEquipmentArrayLambdaVariables {
  const { studioId, equipment, deselectedItems, query, tagsDiff } = args;
  const equipmentObject = pick(equipment, SHARED_FIELDS_GLOBAL);

  const customFields =
    equipment.customFields &&
    Object.values(equipment.customFields).filter(
      (field): field is EquipmentTypeKeepNulls['customFields'][string] =>
        field.content !== undefined,
    );
  return {
    studioId,
    query,
    ...equipmentObject,
    customFields,
    tags: tagsDiff,
    deselectedItems,
  };
}
// Todo: Be more explicit with Interface.
type EquipmentToSend = IPatchEquipmentVariables['data']['equipments'][number];
function mapToPatchArray(
  equipments: PartialEquipmentTypeWithId[],
): EquipmentToSend[] {
  return equipments.map<EquipmentToSend>(each => {
    const equipmentObject = pick(each, SHARED_FIELDS);

    // This may just be filter custom fields that are not dirty
    const $newCustomFields = Object.values(each.customFields ?? {});
    const newCustomFields =
      $newCustomFields.length > 0 ? $newCustomFields : undefined;
    // Some of these may need to be null, ask cherno
    return {
      ...equipmentObject,
      studioId: undefined,
      customFields: newCustomFields,
      tagsId: each.tagsId,
    };
  });
}
